// =============================================================================
// SEVRIC Battery Care — per-user customizable dashboard grid.
// Mirrors the Recon dashboard contract: react-grid-layout under the hood,
// custom widgets via lib/widgetRegistry, persistence via lib/dashboardPrefs.
// =============================================================================
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Responsive, WidthProvider } from "react-grid-layout";
import "react-grid-layout/css/styles.css";
import "react-resizable/css/styles.css";

import { getWidget, widgetsByCategory } from "../lib/widgetRegistry.jsx";
import { loadDashboardPrefs, saveDashboardPrefs, resetDashboardPrefs } from "../lib/dashboardPrefs.js";

const ResponsiveGrid = WidthProvider(Responsive);
const COLS  = { lg: 12, md: 12, sm: 8, xs: 4, xxs: 2 };
const BREAK = { lg: 1200, md: 996, sm: 720, xs: 480, xxs: 0 };
const ROW_HEIGHT = 56;

export function DashboardGrid({ uid, role, loadedAt }) {
  const [layout, setLayout]         = useState(null);
  const [editing, setEditing]       = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [source, setSource]         = useState(null);
  const saveTimer = useRef(0);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const prefs = await loadDashboardPrefs(uid, role);
      if (cancelled) return;
      setLayout(prefs.widgets);
      setSource(prefs.source);
    })();
    return () => { cancelled = true; };
  }, [uid, role, loadedAt]);

  const persist = useCallback((widgets) => {
    if (!uid) return;
    clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => { saveDashboardPrefs(uid, widgets); }, 700);
  }, [uid]);

  const onLayoutChange = useCallback((rglLayout) => {
    if (!layout) return;
    const byId = new Map(layout.map((w) => [w.i, w]));
    const next = rglLayout.map((l) => {
      const prev = byId.get(l.i) || {};
      return { i: l.i, x: l.x, y: l.y, w: l.w, h: l.h, config: prev.config || {} };
    });
    setLayout(next);
    persist(next);
  }, [layout, persist]);

  function addWidget(widgetId) {
    if (!layout) return;
    if (layout.some((w) => w.i === widgetId)) return;
    const def = getWidget(widgetId);
    if (!def) return;
    const maxY = layout.reduce((m, w) => Math.max(m, w.y + w.h), 0);
    const next = [...layout, { i: widgetId, x: 0, y: maxY, w: def.defaultW, h: def.defaultH, config: {} }];
    setLayout(next); persist(next); setDrawerOpen(false);
  }
  function removeWidget(widgetId) {
    if (!layout) return;
    const next = layout.filter((w) => w.i !== widgetId);
    setLayout(next); persist(next);
  }
  async function resetToStarter() {
    if (!uid) return;
    await resetDashboardPrefs(uid);
    const fresh = await loadDashboardPrefs(uid, role);
    setLayout(fresh.widgets); setSource(fresh.source);
  }

  const layouts = useMemo(() => {
    if (!layout) return null;
    return { lg: layout.map((w) => ({ i: w.i, x: w.x, y: w.y, w: w.w, h: w.h, minW: getWidget(w.i)?.minW || 2, minH: getWidget(w.i)?.minH || 2 })) };
  }, [layout]);

  if (!layout) return <div className="dash-loading">Loading your dashboard…</div>;

  const placed = layout.map((w) => ({ ...w, def: getWidget(w.i) })).filter((w) => !!w.def);
  const placedIds = new Set(placed.map((w) => w.i));
  const drawerGroups = Object.entries(widgetsByCategory()).map(([cat, items]) => ({
    cat,
    items: items.filter((w) => !placedIds.has(w.id)),
  })).filter((g) => g.items.length > 0);

  return (
    <div className={`dash-wrap${editing ? ' is-editing' : ''}`}>
      <div className="dash-toolbar">
        <div className="dash-toolbar-l">
          {editing && (
            <span className="dash-edit-hint">Drag to move · drag a corner to resize · click <b>×</b> on a card to remove</span>
          )}
          {!editing && source === "starter-first-load" && (
            <span className="dash-edit-hint">This is your starter view. Tap <b>Customize</b> to make it yours.</span>
          )}
        </div>
        <div className="dash-toolbar-r">
          {editing && (
            <>
              <button className="dash-tb-btn ghost" onClick={() => setDrawerOpen(true)}>+ Add widget</button>
              <button className="dash-tb-btn ghost" onClick={resetToStarter}>Reset to default</button>
              <button className="dash-tb-btn primary" onClick={() => setEditing(false)}>Done</button>
            </>
          )}
          {!editing && (
            <button className="dash-tb-btn ghost" onClick={() => setEditing(true)}>Customize</button>
          )}
        </div>
      </div>

      <ResponsiveGrid
        className="dash-grid"
        layouts={layouts}
        breakpoints={BREAK}
        cols={COLS}
        rowHeight={ROW_HEIGHT}
        margin={[16, 16]}
        containerPadding={[0, 0]}
        isDraggable={editing}
        isResizable={editing}
        useCSSTransforms
        compactType="vertical"
        onLayoutChange={onLayoutChange}
        draggableHandle=".w-shell"
      >
        {placed.map((w) => {
          const Widget = w.def.Component;
          return (
            <div key={w.i} className="dash-cell">
              {editing && (
                <button type="button" className="dash-remove" onClick={() => removeWidget(w.i)} aria-label={`Remove ${w.def.label}`}>×</button>
              )}
              <Widget config={w.config || {}} editing={editing} />
            </div>
          );
        })}
      </ResponsiveGrid>

      {drawerOpen && (
        <>
          <div className="dash-drawer-back" onClick={() => setDrawerOpen(false)} />
          <aside className="dash-drawer" role="dialog" aria-label="Add widget">
            <header className="dash-drawer-head">
              <div className="dash-drawer-eye">SEVRIC · BATTERY · ADD WIDGET</div>
              <h2 className="dash-drawer-h1">Pick what you want <em>visible</em>.</h2>
              <p className="dash-drawer-sub">It lands at the bottom — move it where you want once it's in.</p>
              <button className="dash-drawer-close" onClick={() => setDrawerOpen(false)} aria-label="Close">×</button>
            </header>
            <div className="dash-drawer-body">
              {drawerGroups.length === 0 && <div className="dash-drawer-empty">You've placed every widget already.</div>}
              {drawerGroups.map((g) => (
                <section key={g.cat} className="dash-drawer-cat">
                  <div className="dash-drawer-cat-eye">{g.cat.toUpperCase()}</div>
                  <div className="dash-drawer-tiles">
                    {g.items.map((w) => (
                      <button key={w.id} type="button" className="dash-drawer-tile" onClick={() => addWidget(w.id)}>
                        <div className="dash-drawer-tile-icon">{w.icon}</div>
                        <div className="dash-drawer-tile-label">{w.label}</div>
                        <div className="dash-drawer-tile-desc">{w.description}</div>
                      </button>
                    ))}
                  </div>
                </section>
              ))}
            </div>
          </aside>
        </>
      )}
    </div>
  );
}
